
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { DeliveryZoneDialog } from "./DeliveryZoneDialog";
import { Pencil, Trash } from "lucide-react";
import { useUserProfile } from "@/hooks/useUserProfile";

export const DeliveryZonesList = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState<any>(null);
  const { data: profile } = useUserProfile();

  const { data: zones, refetch } = useQuery({
    queryKey: ["delivery-zones"],
    queryFn: async () => {
      if (!profile?.id) {
        throw new Error("Utilisateur non connecté");
      }

      const { data, error } = await supabase
        .from("delivery_zones")
        .select("*")
        .eq('owner_id', profile.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("delivery_zones")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Zone de livraison supprimée");
      refetch();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleActive = async (id: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from("delivery_zones")
        .update({ is_active: !currentState })
        .eq("id", id);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Zones de livraison</h3>
        <Button onClick={() => {
          setSelectedZone(null);
          setIsDialogOpen(true);
        }}>
          Ajouter une zone
        </Button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Nom</TableHead>
                <TableHead className="min-w-[100px]">Prix</TableHead>
                <TableHead className="min-w-[120px] hidden md:table-cell">Commande minimum</TableHead>
                <TableHead className="min-w-[150px] hidden md:table-cell">Livraison gratuite à partir de</TableHead>
                <TableHead className="min-w-[120px] hidden md:table-cell">Temps estimé</TableHead>
                <TableHead className="min-w-[80px]">Actif</TableHead>
                <TableHead className="min-w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones?.map((zone) => (
                <TableRow key={zone.id} className="group">
                  <TableCell className="font-medium">{zone.name}</TableCell>
                  <TableCell>{zone.price.toLocaleString()} FCFA</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {zone.minimum_order_amount
                      ? `${zone.minimum_order_amount.toLocaleString()} FCFA`
                      : "-"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {zone.free_delivery_threshold
                      ? `${zone.free_delivery_threshold.toLocaleString()} FCFA`
                      : "-"}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{zone.estimated_time || "-"}</TableCell>
                  <TableCell>
                    <Switch
                      checked={zone.is_active}
                      onCheckedChange={() =>
                        handleToggleActive(zone.id, zone.is_active)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedZone(zone);
                          setIsDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">Modifier</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(zone.id)}
                      >
                        <Trash className="h-4 w-4" />
                        <span className="sr-only">Supprimer</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <DeliveryZoneDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        zone={selectedZone}
        onSuccess={() => {
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </div>
  );
};
